/* On-device category history. Never reads account details or sends analytics. */
(() => {
  const KEY = 'homedock_product_interests_v1';
  const MAX_AGE = 30 * 86400000;
  const COUNT_WORDS = { duas: 2, dois: 2, quatro: 4, seis: 6, oito: 8, dez: 10, doze: 12 };
  const normalize = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[-_]/g, ' ').replace(/\s+/g, ' ').trim();
  const quote = (value) => '"' + String(value).replace(/[\\"]/g, '\\$&') + '"';
  const collectionHandle = value => String(value || '').match(/\/collections\/([^/?#]+)/)?.[1] || '';
  const categoryWords = value => normalize(value).split(' ').filter(word => word.length > 2 && !['para', 'com', 'dos', 'das', 'uma'].includes(word)).map(word => word.replace(/s$/, ''));

  function collectionInterest(product) {
    const words = new Set(categoryWords(product.title));
    const collections = (Array.isArray(product.collections) ? product.collections : [])
      .filter(collection => collectionHandle(collection?.url)
        && !/\b(todos|todas|all|home|novidades|lancamentos|destaques|ofertas|promocoes|promo|outlet|black|sale|descontos|oportunidades|aniversario|niver|campanha|frete|vendidos|favoritos)\b/.test(normalize(collection.title)))
      .map(collection => ({ ...collection, relevance: categoryWords(collection.title).filter(word => words.has(word)).length }))
      .sort((a, b) => b.relevance - a.relevance || (Number(a.count) || Infinity) - (Number(b.count) || Infinity));
    const category = collections[0];
    if (!category) return null;
    return { key: 'collection:' + collectionHandle(category.url), label: category.title, query: '', collection: category.url };
  }

  function chairCount(value) {
    const text = normalize(value);
    const match = text.match(/\b(\d{1,2}|duas|dois|quatro|seis|oito|dez|doze)\s*(?:unidades?\s*(?:de\s*)?)?cadeiras?\b/)
      || text.match(/\b(?:kit|conjunto|jogo)\s*(?:com|de|c\/)?\s*(\d{1,2}|duas|dois|quatro|seis|oito|dez|doze)\b/);
    return match ? Number(COUNT_WORDS[match[1]] || match[1]) : null;
  }

  function classify(product) {
    if (!product) return null;
    const title = normalize(product.title), type = normalize(product.type);
    const text = title + ' ' + type;
    let key, query, label;
    // A dining set is evaluated before chairs so the set's chair count cannot
    // turn an entire table-and-chairs set into a chair-only recommendation.
    if (/\bjantar\b/.test(text) && ((/\bmesa\b/.test(text) && /\b(conjunto|cadeiras)\b/.test(text))
      || /\bconjuntos?(?: de)?(?: sala de)? jantar\b/.test(text))) {
      key = 'dining-sets'; label = 'Conjuntos de jantar'; query = 'jantar AND (conjunto OR cadeiras)';
    } else if (/\b(guarda\s*roupas?|roupeiros?)\b/.test(text)) {
      key = 'wardrobes'; label = 'Guarda-roupas'; query = '"guarda roupa" OR "guarda-roupa" OR roupeiro';
    } else if (/\bcadeiras?\b/.test(text) && !/\bmesa\b/.test(title)) {
      const count = chairCount(product.title) || chairCount(product.type);
      // An unknown quantity must not accidentally mix single chairs and kits.
      if (!count && /\b(kit|conjunto|jogo)\b/.test(text)) return null;
      key = 'chairs-' + (count || 1); label = count ? `Conjuntos de ${count} cadeiras` : 'Cadeiras';
      query = count ? `cadeiras AND (${count} OR ${Object.keys(COUNT_WORDS).find(word => COUNT_WORDS[word] === count) || count})` : 'cadeira';
    } else {
      // Every other category follows its Shopify product type. When that field
      // is absent/broad, use a specific catalog collection instead of a campaign.
      if (!type || /^(moveis|mobilia|produtos?|geral|casa|decoracao|furniture)$/.test(type)) return collectionInterest(product);
      key = 'type:' + type; label = product.type; query = 'product_type:' + quote(product.type);
    }
    const matching = (Array.isArray(product.collections) ? product.collections : []).map(collection => {
      if (typeof collection?.url !== 'string') return null;
      const collectionTitle = normalize(collection.title);
      const inferred = classify({ title: collection.title, type: '' });
      let priority = 0;
      if (inferred?.key === key) priority = 1;
      if (collectionTitle === normalize(label)) priority = 2;
      if (type && collectionTitle === type) priority = 3;
      return priority ? { ...collection, priority } : null;
    }).filter(Boolean).sort((a, b) => b.priority - a.priority || (Number(b.count) || 0) - (Number(a.count) || 0));
    const primary = matching[0];
    // Product and collection visits should join the same generic category.
    if (key.startsWith('type:') && primary) key = 'collection:' + collectionHandle(primary.url);
    return { key, label, query, collection: primary?.url || '', alternatives: matching.slice(1, 3).map(collection => collection.url) };
  }

  function matches(product, interest) {
    if (interest.key.startsWith('collection:')) {
      const handle = interest.key.slice('collection:'.length);
      const belongs = Array.isArray(product.collections) && product.collections.some(collection => collectionHandle(collection.url) === handle);
      if (!belongs) return false;
      // A wrong Shopify type/collection assignment must not turn a cristaleira
      // into a buffet. Typed categories also need a category word in the title.
      if (interest.query?.startsWith('product_type:')) {
        const words = new Set(categoryWords(product.title));
        return categoryWords(interest.label).some(word => words.has(word));
      }
      return true;
    }
    return classify(product)?.key === interest.key;
  }

  function prune(history, now = Date.now()) {
    const seen = new Set();
    return (Array.isArray(history) ? history : []).filter(item => {
      if (!item || typeof item.id !== 'string' || !(item.kind === 'collection' ? /^collection:\d+$/ : /^\d+$/).test(item.id) || !item.interest
        || typeof item.interest.key !== 'string' || typeof item.interest.query !== 'string'
        || !Number.isFinite(item.at) || item.at > now || now - item.at >= MAX_AGE || seen.has(item.id)) return false;
      seen.add(item.id); return true;
    }).sort((a, b) => b.at - a.at).slice(0, 20);
  }

  function record(history, product, now = Date.now()) {
    const interest = classify(product);
    const items = prune(history, now);
    if (!interest || !/^\d+$/.test(String(product.id))) return items;
    const kind = product.kind === 'collection' ? 'collection' : 'product';
    const id = (kind === 'collection' ? 'collection:' : '') + String(product.id);
    const previous = items.find(item => item.id === id);
    const catalog = {};
    if (Number.isSafeInteger(product.price) && product.price > 0) catalog.price = product.price;
    if (product.price_basis === 'display') catalog.price_basis = 'display';
    if (typeof product.url === 'string' && /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[a-z0-9][a-z0-9-]*\/?$/i.test(product.url)) catalog.url = product.url;
    return [{ id, kind, interest, ...catalog, at: now, visits: Math.min(5, (previous?.visits || 0) + 1) },
      ...items.filter(item => item.id !== id)].slice(0, 20);
  }

  function groups(history, limit = 3, now = Date.now()) {
    const items = prune(history, now);
    const interests = new Map();
    items.forEach((item, index) => {
      const weight = Math.pow(0.82, index) * Math.pow(0.5, (now - item.at) / (7 * 86400000))
        * (1 + Math.log2(Math.max(1, Math.min(5, Number(item.visits) || 1))) * 0.15);
      const group = interests.get(item.interest.key) || { ...item.interest, weight: 0 };
      if (!group.price && Number.isSafeInteger(item.price) && item.price > 0) {
        group.price = item.price;
        group.price_basis = item.price_basis || 'catalog';
      }
      group.weight += weight;
      interests.set(group.key, group);
    });
    if (!items.length || limit < 1) return [];
    const latest = interests.get(items[0].interest.key);
    const previous = [...interests.values()].filter(group => group.key !== latest.key)
      .sort((a, b) => b.weight - a.weight).slice(0, limit - 1);
    // A recent visit must be represented even when three older categories
    // accumulated more weight. Frequency only chooses the remaining slots.
    return [latest, ...previous];
  }

  function priceRange(interest, margin = 25) {
    const amount = interest?.price;
    if (!Number.isSafeInteger(amount) || amount <= 0) return null;
    const ratio = Math.max(5, Math.min(100, Number(margin) || 25)) / 100;
    return { min: Math.floor(amount * (1 - ratio)), max: Math.ceil(amount * (1 + ratio)) };
  }

  function matchesPrice(product, interest, margin = 25) {
    const range = priceRange(interest, margin);
    return !range || (Number.isSafeInteger(product.price) && product.price >= range.min && product.price <= range.max);
  }

  function mix(pools, limit, excluded = []) {
    const seen = new Set(excluded.map(String));
    const active = pools.filter(pool => pool.cards.length).map(pool => ({ ...pool, cards: [...pool.cards], taken: 0 }));
    const total = active.reduce((sum, pool) => sum + pool.weight, 0);
    const result = [];
    // Give each matching category a place, then apportion the remaining slots.
    while (result.length < limit && active.some(pool => pool.cards.length)) {
      const available = active.filter(pool => pool.cards.length);
      const pool = available.find(pool => pool.taken === 0) || available.sort((a, b) =>
        (b.weight / total * (result.length + 1) - b.taken) - (a.weight / total * (result.length + 1) - a.taken))[0];
      const card = pool.cards.shift();
      if (seen.has(String(card.id))) continue;
      seen.add(String(card.id)); pool.taken++; result.push(card);
    }
    return result;
  }

  function select(pools, limit, excluded = [], viewed = [], preferUnseen = true) {
    if (!preferUnseen) return mix(pools, limit, excluded);
    const seen = new Set(viewed.map(String));
    const ranked = pools.map(pool => ({ ...pool, cards: [
      ...pool.cards.filter(card => !seen.has(String(card.id))),
      ...pool.cards.filter(card => seen.has(String(card.id)))
    ] }));
    // Preserve the category mix, prioritizing fresh options inside each group.
    // A category does not vanish just because all its alternatives were seen.
    return mix(ranked, limit, excluded);
  }

  const api = { KEY, MAX_AGE, normalize, chairCount, classify, matches, priceRange, matchesPrice, prune, record, groups, mix, select };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window === 'undefined' || window.HomedockProductInterests) return;

  let tracked = false;
  let memoryHistory = [];
  let storageBlocked = false;
  let readyResolve;
  api.ready = new Promise(resolve => { readyResolve = resolve; });
  // Storefront recommendations use this browser's local category history.
  // The merchant explicitly made them independent of the cookie banner.
  api.allowed = () => !window.Shopify?.designMode;
  api.read = () => {
    if (!api.allowed()) return [];
    if (storageBlocked) return prune(memoryHistory);
    try {
      const raw = window.localStorage.getItem(KEY);
      const items = prune(JSON.parse(raw || '[]'));
      memoryHistory = items;
      if (raw && raw !== JSON.stringify(items)) {
        if (items.length) window.localStorage.setItem(KEY, JSON.stringify(items));
        else window.localStorage.removeItem(KEY);
      }
      return items;
    } catch { storageBlocked = true; return prune(memoryHistory); }
  };
  api.current = () => {
    try { return JSON.parse(document.querySelector('[data-product-interest-current]')?.dataset.product || 'null'); }
    catch { return null; }
  };
  api.currentCollection = () => {
    try { return JSON.parse(document.querySelector('[data-collection-interest-current]')?.dataset.product || 'null'); }
    catch { return null; }
  };
  window.HomedockProductInterests = api;

  function sync() {
    if (api.allowed()) {
      const history = api.read();
      const current = api.current() || api.currentCollection();
      if (!tracked && current) {
        memoryHistory = record(history, current);
        tracked = true;
        try {
          window.localStorage.setItem(KEY, JSON.stringify(memoryHistory));
        } catch { storageBlocked = true; }
      }
    }
    readyResolve();
    document.dispatchEvent(new CustomEvent('homedock:interests-changed'));
  }

  function start() {
    window.addEventListener('storage', event => { if (event.key === KEY || event.key === null) sync(); });
    window.addEventListener('pageshow', event => { if (event.persisted) sync(); });
    window.addEventListener('focus', sync);
    sync();
  }
  // The async script follows the visit metadata in the document. Do not wait
  // for unrelated deferred apps (including the cookie banner) to finish.
  start();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sync, { once: true });
})();
